import { useQuery } from '@tanstack/react-query';
import {
  cheapestPlanWith,
  findPlan,
  planAllows,
  type PlanFeature,
  type PlanLimit,
} from '@relay-towing/api';
import { driverApi } from '../services/driverApi';
import { useHasPermission } from './useDriverPermissions';
import { useUserCompanies } from './useUserCompanies';

/**
 * Every plan, what it includes and allows: `GET /plans`. It changes only
 * with a deploy or a Stripe price, so it is read once a session.
 */
export const usePlanCatalogue = () =>
  useQuery({
    queryKey: ['plan-catalogue'],
    queryFn: () => driverApi.getPlanCatalogue(),
    staleTime: Infinity,
  });

export const planUsageQueryKey = (companyId: string | null) =>
  ['plan-usage', companyId] as const;

/**
 * The selected company's plan and how much of each limit it has used:
 * `GET /plans/usage`. Read fresh each time a screen asks, since a call or
 * a driver added anywhere changes it.
 */
export const usePlanUsage = () => {
  const { selectedCompanyId } = useUserCompanies();
  return useQuery({
    queryKey: planUsageQueryKey(selectedCompanyId),
    queryFn: () => driverApi.getPlanUsage(selectedCompanyId!),
    enabled: !!selectedCompanyId,
  });
};

/**
 * The selected company's plan, read from its membership, with the
 * catalogue to answer what it includes.
 *
 * Permissions answer "may you?"; the plan answers "would upgrading help?".
 * A control the role excludes is hidden; one the plan excludes offers the
 * upgrade to whoever can buy it (`canUpgrade`, billing's
 * `subscription.view`) and says "Ask your owner to upgrade" to everyone
 * else. The backend refuses either way.
 *
 * `includes` is false until both have answered, which hides a control;
 * wait for `isLoading` before saying the plan lacks something.
 */
export const usePlan = () => {
  const { companies, selectedCompanyId, isLoading } = useUserCompanies();
  const catalogue = usePlanCatalogue();
  const canUpgrade = useHasPermission('subscription.view');

  const plan = companies.find(
    (membership) => membership.company.id === selectedCompanyId
  )?.company.plan;
  const definition =
    plan && catalogue.data ? findPlan(catalogue.data, plan.id) : undefined;

  return {
    /** `{ id, status }`; status `none` is Starter, which nobody pays for. */
    plan,
    /** The name customers see: "Essential", not `STARTER`. */
    name: definition?.name,
    catalogue: catalogue.data,
    isLoading: isLoading || catalogue.isPending,
    /**
     * Whether the company may use a feature now: what its plan includes,
     * or anything while the plan is not enforced (planAllows).
     */
    includes: (feature: PlanFeature) =>
      !!plan && !!catalogue.data && planAllows(catalogue.data, plan, feature),
    /** The cheapest plan with the feature, for "Video is on Pro". */
    upgradeTo: (feature: PlanFeature) =>
      catalogue.data ? cheapestPlanWith(catalogue.data, feature) : null,
    /** The plan's number for a limit; null is unlimited. */
    limitOf: (limit: PlanLimit) =>
      definition ? definition.limits[limit] : undefined,
    canUpgrade,
  };
};
